import datacentral from '@/utils/axios/datacentral'
import {
    ServiceCost,
    CreateServiceCostRequest,
    UpdateServiceCostRequest,
    ServiceCostListParams,
    ServiceCostSummaryParams,
    ServiceCostSummaryItem,
} from '../types'
import { ResponseArray, ResponseObject } from '@/types/shared'
import qs from 'query-string'

const BASE_URL = '/reports/bi/service-costs'

/**
 * Create a new service cost (with automatic upsert behavior)
 * @param data - Service cost data
 * @returns Promise with created service cost
 */
export const createServiceCost = async (
    data: CreateServiceCostRequest
): Promise<ServiceCost> => {
    const response = await datacentral.post<ResponseObject<ServiceCost>>(
        BASE_URL,
        data
    )
    return response.data.result
}

/**
 * Get service cost by ID
 * @param id - Service cost ID
 * @returns Promise with service cost detail
 */
export const getServiceCostById = async (id: number): Promise<ServiceCost> => {
    const { data } = await datacentral.get<ResponseObject<ServiceCost>>(
        `${BASE_URL}/${id}`
    )
    return data.result
}

/**
 * List service costs with optional filters
 * @param params - Filter parameters
 * @returns Promise with service costs list
 */
export const listServiceCosts = async (
    params?: ServiceCostListParams
): Promise<ServiceCost[]> => {
    const queryString = qs.stringify(params || {}, { arrayFormat: 'bracket' })
    const { data } = await datacentral.get<ResponseArray<ServiceCost>>(
        `${BASE_URL}?${queryString}`
    )
    return data?.results || []
}

/**
 * Update an existing service cost
 * @param id - Service cost ID
 * @param data - Updated service cost data
 * @returns Promise with updated service cost
 */
export const updateServiceCost = async (
    id: number,
    data: UpdateServiceCostRequest
): Promise<ServiceCost> => {
    const response = await datacentral.put<ResponseObject<ServiceCost>>(
        `${BASE_URL}/${id}`,
        data
    )
    return response.data.result
}

/**
 * Delete a service cost (soft delete)
 * @param id - Service cost ID
 * @returns Promise
 */
export const deleteServiceCost = async (id: number): Promise<void> => {
    await datacentral.delete(`${BASE_URL}/${id}`)
}

/**
 * Get service cost summary with optional grouping
 * @param params - Summary parameters
 * @returns Promise with service cost summary
 */
export const getServiceCostSummary = async (
    params?: ServiceCostSummaryParams
): Promise<ServiceCostSummaryItem[]> => {
    const queryString = qs.stringify(params || {}, { arrayFormat: 'bracket' })
    const { data } = await datacentral.get<
        ResponseArray<ServiceCostSummaryItem>
    >(`${BASE_URL}/summary?${queryString}`)
    return data?.results || []
}
